// Steering-decision plumbing (Brief 5 pt17) — the pure helpers that turn the
// chat's `aside` messages into the in-app pick-one card's state.
//
// A decision rides INSIDE an `aside` chat message: that message's `body` is the
// SteeringDecision JSON (tagged `kind: "steering_decision"`). When the user
// picks (on ANY surface), the backend posts a SECOND `aside` whose body is a
// SteeringResolved marker (`kind: "steering_resolved"`) carrying the same
// decision_id — so every open card collapses live via the existing SSE fan-out.
//
// These parsers are FAIL-CLOSED: a body that isn't valid JSON, or isn't the
// expected tag, yields null and the card renders nothing (never a broken card) —
// the same posture the backend's redactor/guest-allow-list take on garbled data.

import type { ChatMessage, SteeringDecision, SteeringResolved } from "@waytide/core";

/** Parse an aside message as a steering DECISION, or null if it isn't one. The
 * decision id is the MESSAGE's own id (not in the body), so the card keys its
 * pick on the row that carries it. */
export function parseDecision(message: ChatMessage): SteeringDecision | null {
  const value = safeParse(message.body);
  if (!value || value.kind !== "steering_decision") return null;
  const d = value as Partial<SteeringDecision>;
  if (
    typeof d.thread_id !== "number" ||
    typeof d.prompt !== "string" ||
    !Array.isArray(d.options)
  ) {
    return null;
  }
  // Validate each option shape; drop the card if any is malformed (fail-closed).
  const options = d.options.filter(
    (o): o is { id: string; label: string } =>
      !!o &&
      typeof (o as { id?: unknown }).id === "string" &&
      typeof (o as { label?: unknown }).label === "string",
  );
  if (options.length !== d.options.length) return null;
  return {
    kind: "steering_decision",
    decision_id: message.id,
    thread_id: d.thread_id,
    prompt: d.prompt,
    options,
  };
}

/** Parse an aside message body as a steering RESOLVED marker, or null. */
export function parseResolved(message: ChatMessage): SteeringResolved | null {
  const value = safeParse(message.body);
  if (!value || value.kind !== "steering_resolved") return null;
  const r = value as Partial<SteeringResolved>;
  if (typeof r.decision_id !== "number" || typeof r.via !== "string") return null;
  return { kind: "steering_resolved", decision_id: r.decision_id, via: r.via };
}

/** Whether a message is a steering aside of either kind — used to HIDE the raw
 * JSON aside from the normal message render (the card renders the decision; the
 * resolved marker renders nothing of its own). */
export function isSteeringAside(message: ChatMessage): boolean {
  return parseDecision(message) != null || parseResolved(message) != null;
}

/** The set of decision_ids that have been resolved, from a message list — so a
 * decision card knows to render collapsed even after a reload (the marker is a
 * durable aside) or when another surface answered (the marker arrived live). */
export function resolvedDecisionIds(messages: ChatMessage[]): Set<number> {
  const ids = new Set<number>();
  for (const m of messages) {
    const resolved = parseResolved(m);
    if (resolved) ids.add(resolved.decision_id);
  }
  return ids;
}

/** JSON.parse that never throws — returns the object (with a `kind` field) or
 * null. Bounds the shape to an object so a bare string/number body is rejected. */
function safeParse(body: string): { kind?: string } | null {
  try {
    const value = JSON.parse(body) as unknown;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as { kind?: string };
    }
    return null;
  } catch {
    return null;
  }
}
